import React, { useState } from 'react';
import { createPortal } from 'react-dom';

interface ProviderConflictModalProps {
  isOpen: boolean;
  registeredProvider: string;
  email: string;
  onClose?: () => void;
}

const getProviderDisplayName = (provider: string): string => {
  const p = (provider || '').toLowerCase();
  if (p === 'github') return 'GitHub';
  if (p === 'aad' || p === 'microsoft') return 'Microsoft';
  if (p === 'google') return 'Google';
  return provider.charAt(0).toUpperCase() + provider.slice(1);
};

const getProviderAuthKey = (provider: string): string => {
  const p = (provider || '').toLowerCase();
  if (p === 'microsoft' || p === 'aad') return 'aad';
  if (p === 'google') return 'google';
  return 'github';
};

const ProviderConflictModal: React.FC<ProviderConflictModalProps> = ({
  isOpen,
  registeredProvider,
  email
}) => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  if (!isOpen) return null;

  const providerDisplayName = getProviderDisplayName(registeredProvider);
  const providerKey = getProviderAuthKey(registeredProvider);

  const handleSwitchProvider = async () => {
    setIsRedirecting(true);
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.warn('Storage clear notice', e);
    }
    try {
      await fetch('/.auth/logout', { method: 'GET', mode: 'no-cors', credentials: 'same-origin' });
    } catch (e) {}
    try {
      await fetch('/.auth/logout/complete', { method: 'GET', mode: 'no-cors', credentials: 'same-origin' });
    } catch (e) {}
    const origin = window.location.origin;
    const postLoginTarget = encodeURIComponent(`${origin}/`);
    window.location.replace(`/.auth/login/${providerKey}?post_login_redirect_uri=${postLoginTarget}`);
  };

  const handleSignOut = async () => {
    setIsRedirecting(true);
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      console.warn('Storage clear notice', e);
    }
    try {
      await fetch('/.auth/logout', { method: 'GET', mode: 'no-cors', credentials: 'same-origin' });
    } catch (e) {}
    try {
      await fetch('/.auth/logout/complete', { method: 'GET', mode: 'no-cors', credentials: 'same-origin' });
    } catch (e) {}
    window.location.replace('/');
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[99999] p-4 animate-fade-in select-none">
      <div className="bg-[#0b0f19] border-2 border-amber-500/70 p-6 md:p-8 rounded-3xl max-w-md w-full shadow-[0_0_80px_rgba(245,158,11,0.25)] text-white relative overflow-hidden text-center space-y-5">
        
        {/* Ambient Top Glow Line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"></div>

        {/* Security Shield Icon */}
        <div className="w-14 h-14 bg-amber-500/10 border-2 border-amber-500/40 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
          <svg className="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>

        {/* Header */}
        <div className="space-y-1">
          <span className="text-[9px] font-black uppercase tracking-[0.3em] text-amber-400/90 font-mono">
            Identity Safeguard Protocol
          </span>
          <h2 className="text-base md:text-lg font-black uppercase tracking-tight text-white font-montserrat">
            Authenticate via {providerDisplayName}
          </h2>
        </div>

        {/* Explanatory Context */}
        <div className="bg-[#060911] border border-amber-500/20 p-4 rounded-xl space-y-2 text-left text-xs font-sans">
          <p className="text-gray-300 leading-relaxed">
            The email <span className="text-amber-300 font-mono font-bold break-all">{email || 'associated with this identity'}</span> requires authentication via <span className="text-white font-bold">{providerDisplayName}</span>.
          </p>
          <p className="text-gray-400 text-[11px] leading-relaxed">
            To preserve your digital closet, identity vault, and credits without duplicate accounts, please sign in using {providerDisplayName}.
          </p>
        </div>

        {/* Action Controls */}
        <div className="space-y-3 pt-2">
          <button
            type="button"
            onClick={handleSwitchProvider}
            disabled={isRedirecting}
            className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-black text-xs uppercase tracking-widest rounded-xl transition-all duration-300 shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {isRedirecting ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                <span>Redirecting...</span>
              </>
            ) : (
              <>
                <span>Switch to {providerDisplayName} Login</span>
                <span>&rarr;</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleSignOut}
            disabled={isRedirecting}
            className="w-full py-2.5 px-4 bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white font-bold text-[10px] uppercase tracking-widest rounded-xl border border-white/10 transition-colors cursor-pointer disabled:opacity-50"
          >
            Sign Out & Browse Public
          </button>
        </div>

        <div className="text-[8px] font-mono uppercase tracking-widest text-gray-500">
          Synclothe Clean Identity System • Single Tenant Verification
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProviderConflictModal;
